// ═══════════════════════════════════════════════════════════════════════════
// OPTIMIZED WORKSTATION CREATION DIALOG - NO TYPING DELAY
// ═══════════════════════════════════════════════════════════════════════════

import React, { useState, useMemo, memo } from "react";
import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  Grid,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import FactoryIcon from "@mui/icons-material/Factory";
import { CreateDialog, FieldLabel } from "./shared";

const NEW_GROUP_SENTINEL = "__NEW_GROUP__";

const WorkstationCreateDialog = memo(function WorkstationCreateDialog({
  open,
  onClose,
  onSave,
  saving,
  machines,  // Pass in the machine list to extract existing groups
}) {
  // ✅ Local state - only this dialog re-renders on typing
  const [form, setForm] = useState({
    MachineCode: "",
    MachineGroupCode: "",
    GroupDescription: "",
    MachineDescription: "",
    HoursType: "Machine",
    FinishedGood: 0,
    active_status: 1,
  });
  const [isNewGroup, setIsNewGroup] = useState(false);

  // Extract existing groups from machines
  const existingGroups = useMemo(() => {
    const groupMap = new Map();
    (machines || []).forEach((m) => {
      if (m.MachineGroupCode && !groupMap.has(m.MachineGroupCode)) {
        groupMap.set(m.MachineGroupCode, {
          code: m.MachineGroupCode,
          desc: m.GroupDescription || "",
        });
      }
    });
    return Array.from(groupMap.values()).sort((a, b) =>
      a.code.localeCompare(b.code)
    );
  }, [machines]);

  const h = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleGroupSelect = (e) => {
    const val = e.target.value;
    if (val === NEW_GROUP_SENTINEL) {
      setIsNewGroup(true);
      setForm((f) => ({ ...f, MachineGroupCode: "", GroupDescription: "" }));
    } else {
      setIsNewGroup(false);
      const found = existingGroups.find((g) => g.code === val);
      setForm((f) => ({
        ...f,
        MachineGroupCode: val,
        GroupDescription: found ? found.desc : "",
      }));
    }
  };

  const handleClose = () => {
    // Reset form when closing
    setForm({
      MachineCode: "",
      MachineGroupCode: "",
      GroupDescription: "",
      MachineDescription: "",
      HoursType: "Machine",
      FinishedGood: 0,
      active_status: 1,
    });
    setIsNewGroup(false);
    onClose();
  };

  const handleSave = () => {
    onSave(form);
    // Reset form after save
    setForm({
      MachineCode: "",
      MachineGroupCode: "",
      GroupDescription: "",
      MachineDescription: "",
      HoursType: "Machine",
      FinishedGood: 0,
      active_status: 1,
    });
    setIsNewGroup(false);
  };

  return (
    <CreateDialog
      open={open}
      onClose={handleClose}
      title="Create Workstation"
      icon={<FactoryIcon />}
      color="#2563eb"
    >
      <Grid container spacing={2}>
        <Grid item xs={12} sm={4}>
          <FieldLabel>Machine Code *</FieldLabel>
          <TextField
            fullWidth
            size="small"
            placeholder="e.g. DCM01"
            value={form.MachineCode}
            onChange={h("MachineCode")}
            autoFocus
          />
        </Grid>

        <Grid item xs={12} sm={4}>
          <FieldLabel>Machine Group *</FieldLabel>
          <FormControl fullWidth size="small">
            <Select
              value={isNewGroup ? NEW_GROUP_SENTINEL : form.MachineGroupCode || ""}
              onChange={handleGroupSelect}
              displayEmpty
            >
              <MenuItem value="">Select group</MenuItem>

              {existingGroups.map((g) => (
                <MenuItem key={g.code} value={g.code}>
                  {g.code} — {g.desc}
                </MenuItem>
              ))}

              <MenuItem value={NEW_GROUP_SENTINEL}>+ Create new group</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} sm={4}>
          <FieldLabel>Group Description</FieldLabel>
          <TextField
            fullWidth
            size="small"
            placeholder={isNewGroup ? "e.g. Die Cutting" : "Auto-filled from group"}
            value={form.GroupDescription}
            onChange={h("GroupDescription")}
            disabled={!isNewGroup && !!form.MachineGroupCode}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <FieldLabel>Machine Description *</FieldLabel>
          <TextField
            fullWidth
            size="small"
            value={form.MachineDescription}
            onChange={h("MachineDescription")}
          />
        </Grid>

        <Grid item xs={12} sm={3}>
          <FieldLabel>Hours Type *</FieldLabel>
          <FormControl fullWidth size="small">
            <Select value={form.HoursType} onChange={h("HoursType")}>
              <MenuItem value="Machine">Machine Hours</MenuItem>
              <MenuItem value="Man">Man Hours</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} sm={4}>
          <FieldLabel>Finished Good</FieldLabel>
          <FormControl fullWidth size="small">
            <Select value={form.FinishedGood} onChange={h("FinishedGood")}>
              <MenuItem value={0}>No</MenuItem>
              <MenuItem value={1}>Yes</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} sm={4}>
          <FieldLabel>Active Status</FieldLabel>
          <FormControl fullWidth size="small">
            <Select value={form.active_status} onChange={h("active_status")}>
              <MenuItem value={1}>Active</MenuItem>
              <MenuItem value={0}>Inactive</MenuItem>
            </Select>
          </FormControl>
        </Grid>
      </Grid>

      <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1.5, mt: 3 }}>
        <Button
          onClick={handleClose}
          color="inherit"
          sx={{ textTransform: "none" }}
          disabled={saving}
        >
          Cancel
        </Button>

        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving}
          startIcon={saving ? <CircularProgress size={15} color="inherit" /> : <AddCircleOutlineIcon />}
          sx={{ textTransform: "none", fontWeight: 600, borderRadius: 2 }}
        >
          {saving ? "Creating..." : "Create Workstation"}
        </Button>
      </Box>
    </CreateDialog>
  );
});

export default WorkstationCreateDialog;
